import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Table from "../../Table";
import {
  fetchAllQuestionTypes,
  fetchBoardQuestionsForUploadAnswers,
  resetQuestionForm,
} from "../redux/boardQuestionSlice";
import BoardCascadingDropdowns from "./BoardCascadingDropdowns";
import { BoardMedium_URL } from "../../config";
import showToast from "../../../utils/toast";
import { Toaster } from "react-hot-toast";

const UploadAnswersTab = () => {
  const dispatch = useDispatch();
  const { boardId } = useParams();
  const [selectedType, setSelectedType] = useState(null);

  // Local state for editable question texts and answers
  // format: { [question_id]: { question_text: string, answer: string, isModified: boolean } }
  const [editState, setEditState] = useState({});
  const [isBulkSaving, setIsBulkSaving] = useState(false);

  const {
    uploadAnswersQuestions,
    uploadAnswersCurrentPage,
    uploadAnswersTotalPages,
    uploadAnswersTotalQuestions,
    selectedMedium,
    selectedClass,
    selectedSubject,
    uploadAnswersLoading,
    selectedChapter,
    allQuestionTypes,
  } = useSelector((state) => state.boardQuestion);

  // Fetch questions on component mount and when filters or page change
  useEffect(() => {
    if (
      (boardId && selectedType) ||
      (boardId && selectedMedium && selectedClass && selectedSubject)
    ) {
      dispatch(
        fetchBoardQuestionsForUploadAnswers({
          board_id: boardId,
          page: uploadAnswersCurrentPage,
          limit: 100,
          medium_id: selectedMedium,
          class_id: selectedClass,
          subject_id: selectedSubject,
          chapter_id: selectedChapter,
          question_typeid: selectedType,
        }),
      );
    }
  }, [
    dispatch,
    boardId,
    uploadAnswersCurrentPage,
    selectedMedium,
    selectedClass,
    selectedSubject,
    selectedChapter,
    selectedType,
  ]);

  // Synchronize local editState when uploadAnswersQuestions array updates
  useEffect(() => {
    if (uploadAnswersQuestions && uploadAnswersQuestions.length > 0) {
      const initialMap = {};
      uploadAnswersQuestions.forEach((q) => {
        let initialDetailId = null;
        if (q.options && q.options.length > 0) {
          const correctOpt = q.options.find((o) => o.is_correct === 1);
          if (correctOpt) {
            initialDetailId = correctOpt.detail_id;
          }
        }

        initialMap[q.question_id] = {
          question_id: q.question_id,
          question_typeid: q.question_typeid,
          question_text: q.question_text || "",
          answer: q.answer || "",
          detail_id: initialDetailId,
          answer_explanation: q.answer_explanation || "",
          isModified: false,
        };
      });
      setEditState(initialMap);
    } else {
      setEditState({});
    }
  }, [uploadAnswersQuestions]);

  useEffect(() => {
    dispatch(resetQuestionForm());

    return () => {
      dispatch(resetQuestionForm());
    };
  }, [boardId, dispatch]);

  useEffect(() => {
    dispatch(fetchAllQuestionTypes());
  }, [dispatch]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= uploadAnswersTotalPages) {
      dispatch(
        fetchBoardQuestionsForUploadAnswers({
          board_id: boardId,
          page: newPage,
          limit: 100,
          medium_id: selectedMedium,
          class_id: selectedClass,
          subject_id: selectedSubject,
          chapter_id: selectedChapter,
          question_typeid: selectedType,
        }),
      );
    }
  };

  const handleQuestionTextChange = (questionId, newText) => {
    setEditState((prev) => {
      const current = prev[questionId] || {};
      const original = uploadAnswersQuestions.find(
        (q) => q.question_id === questionId,
      );
      const originalText = original?.question_text || "";
      const originalAns = original?.answer || "";
      const originalExp = original?.answer_explanation || "";
      const isModified =
        newText !== originalText ||
        (current.answer || "") !== originalAns ||
        (current.answer_explanation || "") !== originalExp;

      return {
        ...prev,
        [questionId]: {
          ...current,
          question_text: newText,
          isModified,
        },
      };
    });
  };

  const handleAnswerChange = (questionId, newAnswer, detailId = null) => {
    setEditState((prev) => {
      const current = prev[questionId] || {};
      const original = uploadAnswersQuestions.find(
        (q) => q.question_id === questionId,
      );
      const originalText = original?.question_text || "";
      const originalAns = original?.answer || "";
      const originalExp = original?.answer_explanation || "";
      const originalDetailId =
        original?.options?.find((o) => o.is_correct === 1)?.detail_id || null;

      const isModified =
        (current.question_text || "") !== originalText ||
        newAnswer !== originalAns ||
        (detailId !== null && detailId !== originalDetailId) ||
        (current.answer_explanation || "") !== originalExp;

      return {
        ...prev,
        [questionId]: {
          ...current,
          answer: newAnswer,
          detail_id: detailId !== null ? detailId : current.detail_id,
          isModified,
        },
      };
    });
  };

  const handleExplanationChange = (questionId, newExplanation) => {
    setEditState((prev) => {
      const current = prev[questionId] || {};
      const original = uploadAnswersQuestions.find(
        (q) => q.question_id === questionId,
      );
      const originalText = original?.question_text || "";
      const originalAns = original?.answer || "";
      const originalExp = original?.answer_explanation || "";
      const isModified =
        (current.question_text || "") !== originalText ||
        (current.answer || "") !== originalAns ||
        newExplanation !== originalExp;

      return {
        ...prev,
        [questionId]: {
          ...current,
          answer_explanation: newExplanation,
          isModified,
        },
      };
    });
  };

  const modifiedCount = Object.values(editState).filter(
    (row) => row.isModified,
  ).length;

  const handleSaveAll = async () => {
    const modifiedItems = Object.keys(editState)
      .filter((qid) => editState[qid]?.isModified)
      .map((qid) => {
        const item = editState[qid];
        const res = {
          question_id: parseInt(qid, 10),
          question_typeid: item.question_typeid,
          question_text: item.question_text,
          answer: item.answer,
          answer_explanation: item.answer_explanation,
        };
        if (item.detail_id) {
          res.detail_id = item.detail_id;
        }
        return res;
      });

    if (modifiedItems.length === 0) return;

    setIsBulkSaving(true);
    const toastId = showToast.loading(
      `Saving ${modifiedItems.length} question${modifiedItems.length !== 1 ? "s" : ""}...`,
    );

    try {
      const response = await fetch(
        `${BoardMedium_URL}/bulk-update-question-and-answers`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(modifiedItems),
        },
      );

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || "Failed to save question changes");
      }

      setEditState((prev) => {
        const updated = { ...prev };
        modifiedItems.forEach((item) => {
          if (updated[item.question_id]) {
            updated[item.question_id].isModified = false;
          }
        });
        return updated;
      });

      showToast.dismiss(toastId);
      showToast.success(
        `${modifiedItems.length} question${modifiedItems.length !== 1 ? "s" : ""} saved successfully!`,
      );

      // Re-fetch questions to refresh latest data from server
      dispatch(
        fetchBoardQuestionsForUploadAnswers({
          board_id: boardId,
          page: uploadAnswersCurrentPage,
          limit: 100,
          medium_id: selectedMedium,
          class_id: selectedClass,
          subject_id: selectedSubject,
          chapter_id: selectedChapter,
          question_typeid: selectedType,
        }),
      );
    } catch (error) {
      console.error("Error saving changes:", error);
      showToast.dismiss(toastId);
      showToast.error(error.message || "Failed to save changes");
    } finally {
      setIsBulkSaving(false);
    }
  };

  // Helper to check if a question is True/False
  const isTrueFalseRow = (row) => {
    const typeId = row.question_typeid || row.type_id;
    const typeName = row.type_name?.toLowerCase() || "";
    return (
      String(typeId) === "3" ||
      String(typeId) === "37" ||
      Number(typeId) === 3 ||
      Number(typeId) === 37 ||
      typeName.includes("true") ||
      typeName.includes("false") ||
      typeName.includes("सही") ||
      typeName.includes("सत्य")
    );
  };

  // Table columns: Class, Subject, Chapter, Question Type, Question, Answer, Answer Explanation
  const columns = [
    {
      header: "Class",
      accessor: "class_name",
      render: (val) => (
        <span
          style={{
            fontSize: "12px",
            color: "#374151",
            fontWeight: "500",
            wordBreak: "break-word",
          }}
        >
          {val || "-"}
        </span>
      ),
    },
    {
      header: "Subject",
      accessor: "subject_name",
      render: (val) => (
        <span
          style={{
            fontSize: "12px",
            color: "#374151",
            fontWeight: "500",
            wordBreak: "break-word",
          }}
        >
          {val || "-"}
        </span>
      ),
    },
    {
      header: "Chapter",
      accessor: "chapter_name",
      render: (val) => (
        <span
          style={{
            fontSize: "12px",
            color: "#374151",
            fontWeight: "500",
            wordBreak: "break-word",
          }}
        >
          {val || "-"}
        </span>
      ),
    },
    {
      header: "Question Type",
      accessor: "type_name",
      render: (val) => (
        <span
          style={{
            fontSize: "11px",
            padding: "2px 6px",
            borderRadius: "10px",
            backgroundColor: "#eff6ff",
            color: "#1e40af",
            fontWeight: "500",
            display: "inline-block",
            wordBreak: "break-word",
          }}
        >
          {val || "-"}
        </span>
      ),
    },
    {
      header: "Question",
      accessor: "question_text",
      render: (value, row) => {
        const qid = row.question_id;
        const currentText = editState[qid]?.question_text ?? (value || "");
        const isModified = editState[qid]?.isModified;

        return (
          <div style={{ position: "relative", width: "100%" }}>
            <textarea
              value={currentText}
              onChange={(e) => handleQuestionTextChange(qid, e.target.value)}
              rows={3}
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "8px 10px",
                fontSize: "13px",
                lineHeight: "1.4",
                borderRadius: "6px",
                border: isModified ? "1.5px solid #3b82f6" : "1px solid #d1d5db",
                backgroundColor: isModified ? "#f0fdf4" : "white",
                color: "#1f2937",
                resize: "vertical",
                outline: "none",
                fontFamily: "inherit",
                transition: "border-color 0.2s, background-color 0.2s",
              }}
              placeholder="Enter question text..."
              onFocus={(e) => (e.target.style.borderColor = "#2563eb")}
              onBlur={(e) =>
                (e.target.style.borderColor = isModified ? "#3b82f6" : "#d1d5db")
              }
            />
          </div>
        );
      },
    },
    {
      header: "Answer",
      accessor: "answer",
      render: (value, row) => {
        const qid = row.question_id;
        const currentAns = editState[qid]?.answer ?? (value || "");
        const isModified = editState[qid]?.isModified;

        const isMCQ =
          selectedType === "1" ||
          selectedType === "2" ||
          selectedType === 1 ||
          selectedType === 2 ||
          String(row.question_typeid) === "1" ||
          String(row.question_typeid) === "2" ||
          Number(row.question_typeid) === 1 ||
          Number(row.question_typeid) === 2;

        if (isMCQ) {
          const options = row.options || [];

          if (options.length === 0) {
            return (
              <div style={{ position: "relative", width: "100%" }}>
                <textarea
                  value={currentAns}
                  onChange={(e) => handleAnswerChange(qid, e.target.value)}
                  rows={2}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "6px 8px",
                    fontSize: "12px",
                    borderRadius: "6px",
                    border: isModified
                      ? "1.5px solid #3b82f6"
                      : "1px solid #d1d5db",
                    backgroundColor: isModified ? "#f0fdf4" : "white",
                    outline: "none",
                  }}
                  placeholder="No options found. Enter answer..."
                />
              </div>
            );
          }

          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
                width: "100%",
                padding: "2px 0",
              }}
            >
              {options.map((opt) => {
                const optLetter = String.fromCharCode(
                  64 + (opt.option_sort || 1),
                );
                const normalizedAns = String(currentAns).trim().toUpperCase();
                const isSelected =
                  normalizedAns === optLetter ||
                  normalizedAns === String(opt.option_sort) ||
                  (currentAns === "" && opt.is_correct === 1);

                return (
                  <div
                    key={opt.detail_id || opt.option_sort}
                    onClick={() => handleAnswerChange(qid, optLetter, opt.detail_id)}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                      padding: "6px 10px",
                      borderRadius: "6px",
                      backgroundColor: isSelected ? "#eff6ff" : "#f9fafb",
                      border: isSelected
                        ? "1.5px solid #3b82f6"
                        : "1px solid #e5e7eb",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                      userSelect: "none",
                    }}
                  >
                    {/* Round Circle Radio Indicator */}
                    <div
                      style={{
                        width: "16px",
                        height: "16px",
                        minWidth: "16px",
                        borderRadius: "50%",
                        border: isSelected
                          ? "5px solid #2563eb"
                          : "2px solid #9ca3af",
                        backgroundColor: "white",
                        marginTop: "2px",
                        boxSizing: "border-box",
                        transition: "all 0.15s ease",
                      }}
                    />
                    <div
                      style={{
                        fontSize: "12px",
                        color: isSelected ? "#1e40af" : "#374151",
                        lineHeight: "1.4",
                        wordBreak: "break-word",
                      }}
                    >
                      <strong
                        style={{
                          marginRight: "6px",
                          color: isSelected ? "#1d4ed8" : "#4b5563",
                        }}
                      >
                        {optLetter}.
                      </strong>
                      {opt.text}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        }

        return (
          <div style={{ position: "relative", width: "100%" }}>
            <textarea
              value={currentAns}
              onChange={(e) => handleAnswerChange(qid, e.target.value)}
              rows={3}
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "8px 10px",
                fontSize: "13px",
                lineHeight: "1.4",
                borderRadius: "6px",
                border: isModified ? "1.5px solid #3b82f6" : "1px solid #d1d5db",
                backgroundColor: isModified ? "#f0fdf4" : "white",
                color: "#1f2937",
                resize: "vertical",
                outline: "none",
                fontFamily: "inherit",
                transition: "border-color 0.2s, background-color 0.2s",
              }}
              placeholder="Enter answer..."
              onFocus={(e) => (e.target.style.borderColor = "#2563eb")}
              onBlur={(e) =>
                (e.target.style.borderColor = isModified ? "#3b82f6" : "#d1d5db")
              }
            />
          </div>
        );
      },
    },
    {
      header: "Explanation (Optional)",
      accessor: "answer_explanation",
      render: (value, row) => {
        const qid = row.question_id;
        const currentExp =
          editState[qid]?.answer_explanation ?? (value || "");
        const isModified = editState[qid]?.isModified;

        return (
          <div style={{ position: "relative", width: "100%" }}>
            <textarea
              value={currentExp}
              onChange={(e) => handleExplanationChange(qid, e.target.value)}
              rows={3}
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "8px 10px",
                fontSize: "13px",
                lineHeight: "1.4",
                borderRadius: "6px",
                border: isModified ? "1.5px solid #3b82f6" : "1px solid #d1d5db",
                backgroundColor: isModified ? "#f0fdf4" : "white",
                color: "#1f2937",
                resize: "vertical",
                outline: "none",
                fontFamily: "inherit",
                transition: "border-color 0.2s, background-color 0.2s",
              }}
              placeholder="Enter explanation (optional)..."
              onFocus={(e) => (e.target.style.borderColor = "#2563eb")}
              onBlur={(e) =>
                (e.target.style.borderColor = isModified ? "#3b82f6" : "#d1d5db")
              }
            />
          </div>
        );
      },
    },
  ];

  return (
    <div style={{ padding: "0", maxWidth: "100%", margin: "0 auto" }}>
      <Toaster position="top-right" />
      <style>
        {`
          .upload-answers-filter-container {
            display: flex;
            align-items: flex-end;
            gap: 10px;
            background: #ffffff;
            padding: 10px 14px;
            border-radius: 8px;
            border: 1px solid #e5e7eb;
            margin-bottom: 16px;
            flex-wrap: nowrap;
            overflow-x: auto;
          }

          .upload-answers-filter-container .cascading-dropdown-container {
            padding: 0 !important;
            margin-bottom: 0 !important;
            background: transparent !important;
            border: none !important;
          }

          .upload-answers-filter-container div[style*="display: flex"] {
            display: flex !important;
            gap: 10px !important;
            align-items: flex-end !important;
            margin: 0 !important;
            flex-wrap: nowrap !important;
          }

          .upload-answers-filter-container .row {
            display: flex !important;
            flex-wrap: nowrap !important;
            margin: 0 !important;
            gap: 10px !important;
          }

          .upload-answers-filter-container .col-sm-6 {
            padding: 0 !important;
            width: 140px !important;
            max-width: 155px !important;
            min-width: 120px !important;
            flex: 0 0 auto !important;
            margin: 0 !important;
          }

          .upload-answers-filter-container .cascading-label {
            font-size: 11px !important;
            font-weight: 600 !important;
            color: #374151 !important;
            margin-bottom: 3px !important;
            display: block !important;
            white-space: nowrap !important;
          }

          .upload-answers-filter-container .cascading-select {
            height: 32px !important;
            padding: 4px 8px !important;
            font-size: 12px !important;
            border-radius: 6px !important;
            width: 100% !important;
            border: 1px solid #d1d5db !important;
            background: white !important;
          }

          /* Table column widths styling */
          .upload-answers-table table {
            width: 100%;
            table-layout: fixed;
          }

          /* S.No column (1st) */
          .upload-answers-table th:nth-child(1),
          .upload-answers-table td:nth-child(1) {
            width: 48px !important;
            max-width: 48px !important;
            min-width: 40px !important;
            padding: 8px 4px !important;
            text-align: center !important;
          }

          /* Class column (2nd) */
          .upload-answers-table th:nth-child(2),
          .upload-answers-table td:nth-child(2) {
            width: 80px !important;
            max-width: 90px !important;
            min-width: 65px !important;
            padding: 8px 6px !important;
          }

          /* Subject column (3rd) */
          .upload-answers-table th:nth-child(3),
          .upload-answers-table td:nth-child(3) {
            width: 95px !important;
            max-width: 110px !important;
            min-width: 75px !important;
            padding: 8px 6px !important;
          }

          /* Chapter column (4th) */
          .upload-answers-table th:nth-child(4),
          .upload-answers-table td:nth-child(4) {
            width: 110px !important;
            max-width: 125px !important;
            min-width: 85px !important;
            padding: 8px 6px !important;
          }

          /* Question Type column (5th) */
          .upload-answers-table th:nth-child(5),
          .upload-answers-table td:nth-child(5) {
            width: 100px !important;
            max-width: 115px !important;
            min-width: 80px !important;
            padding: 8px 6px !important;
          }

          /* Question column (6th) - equal share */
          .upload-answers-table th:nth-child(6),
          .upload-answers-table td:nth-child(6) {
            width: calc((100% - 433px) / 3) !important;
            min-width: 200px !important;
            padding: 8px 10px !important;
          }

          /* Answer column (7th) - equal share */
          .upload-answers-table th:nth-child(7),
          .upload-answers-table td:nth-child(7) {
            width: calc((100% - 433px) / 3) !important;
            min-width: 200px !important;
            padding: 8px 10px !important;
          }

          /* Answer Explanation column (8th) - equal share */
          .upload-answers-table th:nth-child(8),
          .upload-answers-table td:nth-child(8) {
            width: calc((100% - 433px) / 3) !important;
            min-width: 200px !important;
            padding: 8px 10px !important;
          }

          .pagination-container {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 8px;
            margin-top: 24px;
            padding: 20px;
            background: white;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }

          .pagination-btn {
            background: white;
            border: 1px solid #d1d5db;
            color: #374151;
            padding: 8px 14px;
            border-radius: 6px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
            transition: all 0.2s ease;
            min-width: 38px;
            height: 38px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .pagination-btn:hover:not(:disabled) {
            background: #f3f4f6;
            border-color: #3498db;
            color: #3498db;
          }

          .pagination-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          .pagination-btn.active {
            background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
            color: white;
            border-color: #3498db;
            font-weight: 600;
          }

          .pagination-info {
            padding: 8px 16px;
            background: #f8f9fa;
            border-radius: 6px;
            font-size: 13px;
            color: #6b7280;
            font-weight: 500;
          }

          .table-scroll-container::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }

          .table-scroll-container::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 4px;
          }

          .table-scroll-container::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 4px;
          }

          .table-scroll-container::-webkit-scrollbar-thumb:hover {
            background: #555;
          }
        `}
      </style>

      {/* FILTERS AND SINGLE SAVE CHANGES BUTTON IN ONE ROW */}
      <div className="upload-answers-filter-container">
        <div style={{ display: "flex", gap: "10px", alignItems: "flex-end", flex: "1 1 auto", flexWrap: "nowrap" }}>
          <BoardCascadingDropdowns
            boardId={boardId}
            showTitle={false}
            layout="main"
          />
          <div style={{ width: "140px", maxWidth: "155px", minWidth: "120px", flex: "0 0 auto", marginBottom: "0" }}>
            <label className="cascading-label">Question Type</label>
            <select
              value={selectedType || ""}
              onChange={(e) => setSelectedType(e.target.value)}
              className="cascading-select"
            >
              <option value="">-- Question Type --</option>
              {allQuestionTypes?.map((each) => (
                <option key={each.type_id} value={each.type_id}>
                  {each.type_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Single "Save Changes" Button */}
        <div style={{ flexShrink: 0 }}>
          <button
            onClick={handleSaveAll}
            disabled={isBulkSaving || modifiedCount === 0}
            style={{
              height: "32px",
              padding: "0 16px",
              backgroundColor:
                modifiedCount > 0
                  ? isBulkSaving
                    ? "#93c5fd"
                    : "#2563eb"
                  : "#9ca3af",
              color: "white",
              border: "none",
              borderRadius: "6px",
              fontSize: "13px",
              fontWeight: "600",
              cursor:
                modifiedCount > 0 && !isBulkSaving ? "pointer" : "not-allowed",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              whiteSpace: "nowrap",
              boxShadow:
                modifiedCount > 0
                  ? "0 2px 6px rgba(37, 99, 235, 0.3)"
                  : "none",
              opacity: modifiedCount === 0 ? 0.6 : 1,
              transition: "all 0.2s",
            }}
          >
            <span>💾</span>
            {isBulkSaving
              ? "Saving..."
              : modifiedCount > 0
              ? `Save Changes (${modifiedCount})`
              : "Save Changes"}
          </button>
        </div>
      </div>

      {/* QUESTIONS TABLE */}
      {uploadAnswersLoading ? (
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            backgroundColor: "white",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>⏳</div>
          <p style={{ fontSize: "16px", color: "#6b7280" }}>
            Loading questions...
          </p>
        </div>
      ) : uploadAnswersQuestions.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            backgroundColor: "white",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>📋</div>
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: "#111827",
              marginBottom: "8px",
            }}
          >
            No Questions Found
          </h3>
          <p style={{ fontSize: "14px", color: "#6b7280", margin: 0 }}>
            Select the filters above to view and edit questions.
          </p>
        </div>
      ) : (
        <>
          <div
            className="table-scroll-container upload-answers-table"
            style={{
              maxHeight: "650px",
              overflowY: "auto",
              overflowX: "auto",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              backgroundColor: "white",
            }}
          >
            <Table
              columns={columns}
              data={uploadAnswersQuestions}
              showSerialNumber={true}
            />
          </div>

          {/* PAGINATION */}
          {uploadAnswersTotalPages > 1 && (
            <div className="pagination-container">
              {/* Previous Button */}
              <button
                onClick={() => handlePageChange(uploadAnswersCurrentPage - 1)}
                disabled={uploadAnswersCurrentPage === 1}
                className="pagination-btn"
                title="Previous page"
              >
                ←
              </button>

              {/* Page Numbers */}
              {Array.from(
                { length: uploadAnswersTotalPages },
                (_, i) => i + 1,
              ).map((page) => {
                const showPage =
                  page === 1 ||
                  page === uploadAnswersTotalPages ||
                  (page >= uploadAnswersCurrentPage - 1 &&
                    page <= uploadAnswersCurrentPage + 1);

                if (!showPage) {
                  if (
                    page === uploadAnswersCurrentPage - 2 ||
                    page === uploadAnswersCurrentPage + 2
                  ) {
                    return (
                      <span
                        key={page}
                        style={{
                          padding: "8px 4px",
                          color: "#9ca3af",
                          fontSize: "14px",
                        }}
                      >
                        ...
                      </span>
                    );
                  }
                  return null;
                }

                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`pagination-btn ${page === uploadAnswersCurrentPage ? "active" : ""}`}
                  >
                    {page}
                  </button>
                );
              })}

              {/* Next Button */}
              <button
                onClick={() => handlePageChange(uploadAnswersCurrentPage + 1)}
                disabled={uploadAnswersCurrentPage === uploadAnswersTotalPages}
                className="pagination-btn"
                title="Next page"
              >
                →
              </button>

              {/* Page Info */}
              <div className="pagination-info">
                Page {uploadAnswersCurrentPage} of {uploadAnswersTotalPages}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UploadAnswersTab;
